import * as React from 'react'
import { message, notification } from 'antd'

// ============= Ant Design Toast 封装 =============
// 提供与 sonner 兼容的 API

type ToastType = 'success' | 'error' | 'info' | 'warning'

interface ToastOptions {
  description?: string
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
}

/** 对象形式调用（兼容 shadcn/ui 签名） */
interface ToastObjectArg {
  title?: string
  description?: string
  variant?: 'default' | 'destructive' | 'success' | 'warning' | string
  duration?: number
}

// toast 函数 - 同时支持字符串和对象两种调用方式
function toast(messageOrOptions: string | ToastObjectArg, options?: ToastOptions) {
  if (typeof messageOrOptions === 'string') {
    notification.info({
      message: messageOrOptions,
      description: options?.description,
      duration: options?.duration ?? 3,
    })
    return
  }

  // 对象形式：根据 variant 映射到对应 notification 方法
  const { title, description, variant, duration } = messageOrOptions
  const method = variant === 'destructive' ? 'error'
    : variant === 'success' ? 'success'
    : variant === 'warning' ? 'warning'
    : 'info'
  notification[method]({
    message: title,
    description,
    duration: duration ?? (method === 'error' ? 5 : 3),
  })
}

// 扩展 toast 方法
toast.success = (msg: string, options?: ToastOptions) => {
  notification.success({
    message: msg,
    description: options?.description,
    duration: options?.duration ?? 3,
  })
}

toast.error = (msg: string, options?: ToastOptions) => {
  notification.error({
    message: msg,
    description: options?.description,
    duration: options?.duration ?? 5,
  })
}

toast.info = (msg: string, options?: ToastOptions) => {
  notification.info({
    message: msg,
    description: options?.description,
    duration: options?.duration ?? 3,
  })
}

toast.warning = (msg: string, options?: ToastOptions) => {
  notification.warning({
    message: msg,
    description: options?.description,
    duration: options?.duration ?? 4,
  })
}

toast.loading = (msg: string, options?: ToastOptions) => {
  const key = `loading_${Date.now()}`
  message.loading({ content: msg, key, duration: 0 })
  return key
}

toast.dismiss = (key?: string) => {
  if (key) {
    message.destroy(key)
  } else {
    message.destroy()
  }
}

toast.promise = async <T,>(
  promise: Promise<T>,
  options: {
    loading: string
    success: string | ((data: T) => string)
    error: string | ((err: any) => string)
  }
): Promise<T> => {
  const key = `promise_${Date.now()}`
  message.loading({ content: options.loading, key, duration: 0 })

  try {
    const result = await promise
    const successMsg =
      typeof options.success === 'function' ? options.success(result) : options.success
    message.success({ content: successMsg, key, duration: 3 })
    return result
  } catch (error) {
    const errorMsg = typeof options.error === 'function' ? options.error(error) : options.error
    message.error({ content: errorMsg, key, duration: 5 })
    throw error
  }
}

// Toaster 组件（兼容性占位符，Ant Design 自带全局消息机制）
function Toaster() {
  return null
}

export { toast, Toaster }
